import { DropLocation, DropPoint } from '@m4a3/api-client';
import { espooCellId, kerryCellId } from '../constants';

// Ensure DropPointContext.drop_points is always a flat DropPoint[]
export const normalizeDropPoints = (
  dropPoints: DropPoint[] | { data: DropPoint[] } | undefined
): DropPoint[] => {
  if (Array.isArray(dropPoints)) return dropPoints;
  if (
    dropPoints &&
    typeof dropPoints === 'object' &&
    'data' in (dropPoints as any) &&
    Array.isArray((dropPoints as any).data)
  ) {
    return (dropPoints as any).data as DropPoint[];
  }
  return [];
};

// Cell IDs that have special drop point restrictions
export const RESTRICTED_DROP_POINT_CELLS = {
  ESPOO: espooCellId,           // Only VERIFIED risk drop points allowed
} as const;

export type RestrictedCellId = typeof RESTRICTED_DROP_POINT_CELLS[keyof typeof RESTRICTED_DROP_POINT_CELLS];

/**
 * Check if a cell requires only predefined drop points (no new creation allowed inside the route confirmation screen)
 */
export const isPredefineDropPointsOnlyCell = (cellId: number): boolean => {
  return false; //Currently we have no cells of this type 
};

/**
 * Check if a cell requires that all drop points have been verified for risk
 */
export const isVerifiedRiskOnlyCell = (cellId: number): boolean => {
  return cellId === RESTRICTED_DROP_POINT_CELLS.ESPOO;
};

/**
 * Check if a drop point should be disabled for selection based on cell restrictions
 */
export const isDropPointDisabledForCell = (cellId: number, dropPoint: DropLocation): boolean => {
  // Espoo (cell 12): Only allow VERIFIED risk drop points
  if (isVerifiedRiskOnlyCell(cellId)) {
    return dropPoint?.risk !== 'VERIFIED';
  }
  
  // Add other cell-specific restrictions here as needed
  
  return false;
};

/**
 * Check if new drop point creation should be disabled for a cell
 */
export const isDropPointCreationDisabled = (cellId: number): boolean => {
  return isPredefineDropPointsOnlyCell(cellId) || isVerifiedRiskOnlyCell(cellId);
};

/**
 * Get the user-friendly cell name for restriction messages
 */
export const getCellNameForRestrictions = (cellId: number): string => {
  switch (cellId) {
    case RESTRICTED_DROP_POINT_CELLS.ESPOO:
      return 'Espoo';
    default:
      return 'this cell';
  }
};

/**
 * Get appropriate restriction message for a cell
 */
export const getDropPointRestrictionMessage = (cellId: number): string => {
  const cellName = getCellNameForRestrictions(cellId);
  
  if (isPredefineDropPointsOnlyCell(cellId)) {
    return `Cannot create new drop points for ${cellName}. Please select from predefined drop points only.`;
  }
  
  if (isVerifiedRiskOnlyCell(cellId)) {
    return `Only verified drop points can be selected for ${cellName}.`;
  }
  
  return `Drop point restrictions apply for ${cellName}.`;
}; 